import React, { useEffect, useState } from 'react';
import { View, Text } from '@aws-amplify/ui-react';
import { Box, CircularProgress } from '@mui/material';
import { StyledSelect, StyledMenuItem } from './StyledInputs';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import './reports.css';
import { useNavigate } from 'react-router';
import { DateRangePicker } from './reportsUtils';
import ReportsCharts from './ReportsCharts';
import SelectionModal from './SelectionModal';
import { GamifyToast } from '../../common/CustomToasts';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import useListUsers from '../../hooks/useListUsers';
import useListTeams from '../../hooks/useListTeams';
import useListKPIs from '../../hooks/useListKPIs';

export default function ReportsScreen() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const orgID = global.me.orgID;
  const now = new Date();
  const reportTypes = [
    { value: 'org', title: 'Organization' },
    { value: 'team', title: 'Teams' },
    { value: 'user', title: 'Users' },
    { value: 'hourly', title: 'Hourly' },
  ];
  // const url = 'http://localhost:3001/kpireport';
  const url = 'https://api.gogamify.com/report/kpireport';

  const [searchString, setSearchString] = useState('');
  const [showReportConfig, setShowReportConfig] = useState(true);
  const [gettingReports, setGettingReports] = useState(false);
  const [checkedUsers, setCheckedUsers] = useState({});
  const [checkedTeams, setCheckedTeams] = useState({});
  const [checkedKpis, setCheckedKpis] = useState({});
  const [reportType, setReportType] = useState('org');
  const [modalType, setModalType] = useState(null);
  const [rangeCode, setRangeCode] = useState('T');
  const [canSubmit, setCanSubmit] = useState(false);
  const [fromTime, setFromTime] = useState(
    new Date(now.getFullYear(), now.getMonth(), now.getDate())
  );
  const [toTime, setToTime] = useState(
    new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  );
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  // Data hooks — cached via tanstack query
  const { data: usersList } = useListUsers(searchString || '*');
  const { data: teamsList } = useListTeams(searchString || '*');
  const { data: kpisList } = useListKPIs();

  const modalConfiguration = {
    user: {
      type: 'users',
      fetchFunction: async () => {},
      optionsList: usersList,
      checkedList: checkedUsers,
      checkedListSetter: (id, name) => {
        const temp = { ...checkedUsers };
        if (temp[id]) {
          delete temp[id];
        } else {
          temp[id] = name;
        }
        setCheckedUsers(temp);
      },
      useAvatar: true,
      selectAll: () =>
        setCheckedUsers(
          usersList.reduce((acc, el) => {
            acc[el.id] = el.name;
            return acc;
          }, {})
        ),
      clearAll: () => setCheckedUsers({}),
      fetchOnStringChange: false,
    },
    team: {
      type: 'teams',
      fetchFunction: async () => {},
      optionsList: teamsList,
      checkedList: checkedTeams,
      checkedListSetter: (id, name) => {
        const temp = { ...checkedTeams };
        if (temp[id]) {
          delete temp[id];
        } else {
          temp[id] = name;
        }
        setCheckedTeams(temp);
      },
      useAvatar: true,
      selectAll: () =>
        setCheckedTeams(
          teamsList.reduce((acc, el) => {
            acc[el.id] = el.name;
            return acc;
          }, {})
        ),
      clearAll: () => setCheckedTeams({}),
      fetchOnStringChange: false,
    },
    kpi: {
      type: 'KPIs',
      fetchFunction: async () => {},
      optionsList: kpisList,
      checkedList: checkedKpis,
      checkedListSetter: (id, name) => {
        const temp = { ...checkedKpis };
        if (temp[id]) {
          delete temp[id];
        } else {
          temp[id] = name;
        }
        setCheckedKpis(temp);
      },
      useAvatar: false,
      selectAll: () =>
        setCheckedKpis(
          kpisList.reduce((acc, el) => {
            acc[el.id] = el.name;
            return acc;
          }, {})
        ),
      clearAll: () => setCheckedKpis({}),
      fetchOnStringChange: false,
    },
  };
  const [myReports, setMyReports] = useState(null);

  const handleRunReport = async () => {
    if (fromTime >= toTime) {
      GamifyToast.error(
        'Invalid Time Period',
        "From date can't be later than to date"
      );
      return;
    }
    setGettingReports(true);

    // Create base payload
    const basePayload = { org_id: orgID, report_type: reportType, dates: {} };

    // Convert times to UTC
    const fromTimeUTC = new Date(
      fromTime.getTime() - fromTime.getTimezoneOffset() * 60000
    );
    const toTimeUTC = new Date(
      toTime.getTime() - toTime.getTimezoneOffset() * 60000
    );

    basePayload.dates.from = fromTimeUTC.toISOString();
    basePayload.dates.to = toTimeUTC.toISOString();

    // Add users for user & hourly reports
    if (['user', 'hourly'].includes(reportType)) {
      basePayload.users = Object.keys(checkedUsers);
    }

    // Add teams for team reports
    if (reportType === 'team') {
      basePayload.teams = Object.keys(checkedTeams);
    }

    const kpisList = Object.keys(checkedKpis);

    try {
      let combinedReport = null;

      // ===================================================================
      // IMPORTANT: Special handling for User Reports to prevent payload size issues
      // ===================================================================
      // User reports can generate extremely large response payloads when multiple KPIs
      // are requested at once. This can exceed AWS Lambda payload limits (6MB).
      // Instead, we fetch each KPI individually and combine the results.
      // DO NOT REMOVE THIS LOGIC without understanding the payload size implications.
      // ===================================================================
      if (reportType === 'user' && kpisList.length > 1) {
        // Process user reports silently without progress notifications

        // Create queue to process KPIs one at a time
        let errorCount = 0;

        // Process each KPI individually
        for (const kpiId of kpisList) {
          try {
            // Create single KPI payload
            const kpiPayload = { ...basePayload, KPIs: [kpiId] };

            // Fetch data for this KPI
            const response = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(kpiPayload),
            });

            if (!response.ok) {
              console.error(
                `KPI ${kpiId} request failed with status: ${response.status}`
              );
              errorCount++;
              continue; // Skip this KPI but continue with others
            }

            const kpiReport = await response.json();

            // If this is our first successful report, use it as the base structure
            if (!combinedReport) {
              // Create a deep copy of the first report
              combinedReport = JSON.parse(JSON.stringify(kpiReport));

              // Ensure reports field is properly initialized based on its original type
              if (!combinedReport.reports) {
                combinedReport.reports = Array.isArray(kpiReport.reports)
                  ? []
                  : {};
              } else if (Array.isArray(combinedReport.reports)) {
                // Clear any initial reports to avoid duplication - we'll add them back
                combinedReport.reports = [];
              } else {
                // For object-style reports, start with an empty object
                combinedReport.reports = {};
              }
            }

            // Add the reports from this KPI to the combined result
            if (kpiReport.reports) {
              if (
                Array.isArray(kpiReport.reports) &&
                Array.isArray(combinedReport.reports)
              ) {
                // Array type - concat the reports
                combinedReport.reports.push(...kpiReport.reports);
              } else if (
                !Array.isArray(kpiReport.reports) &&
                !Array.isArray(combinedReport.reports)
              ) {
                // Object type - merge the reports
                Object.keys(kpiReport.reports).forEach((key) => {
                  combinedReport.reports[key] = kpiReport.reports[key];
                });
              } else {
                console.error(
                  'Report structure mismatch between KPI responses'
                );
              }
            }
          } catch (err) {
            console.error(`Error fetching KPI ${kpiId}:`, err);
            errorCount++;
          }
        }

        // Notify about any errors
        if (errorCount > 0) {
          GamifyToast.warning(
            'Partial Data Retrieved',
            `${errorCount} out of ${kpisList.length} KPIs failed to load.`
          );
        }

        // If we couldn't get any data, show error
        if (!combinedReport) {
          throw new Error('Failed to retrieve any report data');
        }
      } else {
        // Standard approach for other report types - fetch all KPIs at once
        const ourPayload = { ...basePayload, KPIs: kpisList };

        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ourPayload),
        });

        if (res.error) {
          throw new Error(res.error);
        }

        if (!res.ok) {
          if (res.status === 502) {
            setShowReportConfig(false);
            GamifyToast.error(
              'No Data Available',
              'No data found for the selected date range. Please try a different time period.'
            );
            return;
          } else {
            const errorText = await res.text();
            throw new Error(`API Error (${res.status}): ${errorText}`);
          }
        }

        combinedReport = await res.json();
      }

      // Check if reports array is empty
      if (
        combinedReport.reports &&
        ((Array.isArray(combinedReport.reports) &&
          combinedReport.reports.length === 0) ||
          (typeof combinedReport.reports === 'object' &&
            Object.keys(combinedReport.reports).length === 0))
      ) {
        GamifyToast.error(
          'No Data Available',
          'No data found for the selected date range. Please try a different time period.'
        );
      }

      setMyReports(combinedReport);
      setShowReportConfig(false);
    } catch (err) {
      console.error('Report generation error:', err);
      GamifyToast.error(
        'Error Generating Report',
        'Please try a different time period or configuration.'
      );
    } finally {
      setGettingReports(false);
    }
  };

  const reportConfigString = () => {
    let temp = reportType[0].toUpperCase() + reportType.slice(1) + ' Report > ';
    if (reportType !== 'hourly') {
      const kpis = Object.values(checkedKpis);
      temp += kpis.slice(0, 3).join(', ');
      if (kpis.length >= 3) temp += '...';
      temp += ' > ';
    }
    if (reportType === 'user' || reportType === 'hourly') {
      const users = Object.values(checkedUsers);
      temp += users.slice(0, 3).join(', ');
      if (users.length >= 3) temp += '...';
      temp += ' > ';
    }
    if (reportType === 'team') {
      const teams = Object.values(checkedTeams);
      temp += teams.slice(0, 3).join(',');
      if (teams.length >= 3) temp += '...';
      temp += ' > ';
    }
    const newFrom = fromTime.toISOString().split('T')[0].split('-');
    temp += `${newFrom[1]}/${newFrom[2]}/${newFrom[0]} - `;
    const newTo = toTime.toISOString().split('T')[0].split('-');
    temp += `${newTo[1]}/${newTo[2]}/${newTo[0]}`;
    if (reportType === 'hourly') {
      temp += ` > ${fromTime.getHours()}:00 - ${toTime.getHours()}:00`;
    }

    return temp;
  };

  const exportFullReportCSV = async () => {
    if (!myReports) {
      GamifyToast.error(
        'No Data Available',
        'No data found for the selected date range. Please try a different time period.'
      );
      return;
    }

    // Start with loading indicator
    GamifyToast.info(
      'Processing Report',
      'Starting export process. This may take a while for large date ranges.'
    );

    try {
      setIsExporting(true);

      // Get pagination parameters from current report
      const userIDs = Object.keys(checkedUsers);
      const fromTimeUTC = new Date(
        fromTime.getTime() - fromTime.getTimezoneOffset() * 60000
      );
      let toTimeUTC = new Date(
        toTime.getTime() - toTime.getTimezoneOffset() * 60000
      );

      // For CSV exports, we need to cap the end date to prevent fetching from the future
      const currentTime = new Date();
      if (toTimeUTC > currentTime) {
        toTimeUTC = new Date(currentTime);
        GamifyToast.info(
          'Date Range Adjusted',
          'Export will include data up to the current time only.'
        );
      }

      // Calculate total weeks in the date range
      const totalDays = Math.ceil(
        (toTimeUTC - fromTimeUTC) / (1000 * 60 * 60 * 24)
      );
      const totalWeeks = Math.ceil(totalDays / 7);

      // URL for the new paginated endpoint

      // State to track and combine all fetched records
      let allRecords = [];

      // Process week by week
      let currentWeekStart = new Date(fromTimeUTC);
      let weekCounter = 0;

      while (currentWeekStart < toTimeUTC) {
        // Calculate end of current week (or use toTimeUTC if it's sooner)
        const currentWeekEnd = new Date(currentWeekStart);
        currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
        if (currentWeekEnd > toTimeUTC) {
          currentWeekEnd.setTime(toTimeUTC.getTime());
        }

        // Fetch data for current week
        let hasMoreData = true;
        let nextToken = null;

        while (hasMoreData) {
          const payload = {
            report_type: 'hourly-export',
            org_id: orgID,
            users: userIDs,
            dates: {
              from: currentWeekStart.toISOString(),
              to: currentWeekEnd.toISOString(),
            },
            pageSize: 500, // Reasonable chunk size that won't timeout
            nextToken,
          };

          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
          }

          const data = await response.json();

          // Add records from this page to our collection
          if (data.records && data.records.length > 0) {
            allRecords = [...allRecords, ...data.records];
          }

          // Check if more data is available for this week
          // For hourly exports, we need to ensure the nextToken is not in the future
          if (data.nextToken) {
            try {
              // The nextToken is expected to be a timestamp (milliseconds since epoch)
              const tokenValue = parseInt(data.nextToken, 10);

              if (!isNaN(tokenValue)) {
                const tokenDate = new Date(tokenValue);
                const currentDate = new Date();

                if (tokenDate > currentDate) {
                  hasMoreData = false;
                } else {
                  nextToken = data.nextToken;
                }
              } else {
                // If the token isn't a valid number, use it anyway (server will handle validation)
                nextToken = data.nextToken;
              }
            } catch (error) {
              console.error('Error parsing nextToken:', error);
              // If we can't parse it, use the token as-is and let the server validate
              nextToken = data.nextToken;
            }
          } else {
            hasMoreData = false;
          }
        }

        // Update to the next week
        currentWeekStart = new Date(
          currentWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000
        );
        weekCounter++;
        // Update progress percentage
        const progressPercent = Math.min(
          100,
          Math.floor((weekCounter / totalWeeks) * 100)
        );
        setExportProgress(progressPercent);
      }

      // Once all data is retrieved, generate CSV
      let csvString = `,User Name,Date,Time,Pin,Address,Distance From Door\r\n`;

      // Use allRecords if we fetched paginated data, otherwise fall back to the full_hourly_report
      const recordsToExport =
        allRecords.length > 0 ? allRecords : myReports.full_hourly_report;

      recordsToExport.forEach((dataset, i) => {
        const addressWithCommas = `"${dataset.address}"`;

        csvString += `${i + 1},${dataset.user_name},${dataset.pin_date},${
          dataset.pin_time
        },${dataset.pin},${addressWithCommas},${
          dataset.distance_from_door || 'Not Recorded'
        }\r\n`;
      });

      let exportFileName = ``;
      exportFileName += `${myReports.reports[0].report_info.report_name}`;
      exportFileName += `_${
        myReports.reports[myReports.reports.length - 1].report_info.report_name
      }`;
      exportFileName += `_pin_dropping_report_${myReports.org_id}`;

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileUrl = URL.createObjectURL(blob);
      link.setAttribute('href', fileUrl);
      link.setAttribute('download', exportFileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Reset export state
      setIsExporting(false);
      setExportProgress(0);

      GamifyToast.success(
        'Export Complete',
        `Successfully exported ${recordsToExport.length} records.`
      );
    } catch (error) {
      console.error('Error exporting report:', error);
      GamifyToast.error(
        'Export Failed',
        'There was an error exporting your report. Please try again.'
      );
      setIsExporting(false);
      setExportProgress(0);
    }
  };
  useEffect(() => {
    if (global.me.role !== 'admin' || !global.me.isAdmin) navigate(-1);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      theme.isDark ? 'dark' : 'light'
    );
  }, [theme.isDark]);

  useEffect(() => {
    if (Object.values(checkedKpis).length === 0 && reportType !== 'hourly') {
      setCanSubmit(false);
      return;
    }
    if (reportType === 'team' && Object.values(checkedTeams).length === 0) {
      setCanSubmit(false);
      return;
    }
    if (
      ['user', 'hourly'].includes(reportType) &&
      Object.values(checkedUsers).length === 0
    ) {
      setCanSubmit(false);
      return;
    }
    setCanSubmit(true);
  }, [checkedKpis, checkedTeams, checkedUsers, reportType, fromTime, toTime]);

  // Add effect to clear reports data when settings change
  useEffect(() => {
    // Clear existing reports when any settings change
    if (myReports) {
      setMyReports(null);
      setShowReportConfig(true);
    }
  }, [
    reportType,
    checkedUsers,
    checkedTeams,
    checkedKpis,
    fromTime,
    toTime,
    rangeCode,
  ]);

  return (
    <React.Fragment>
      <View
        style={{
          minHeight: '100vh',
          paddingBottom: '50px',
          backgroundColor: 'var(--background-color)',
        }}
      >
        {myReports && myReports.report_type === 'hourly' && (
          <Box
            className="generate-button export_full_report_button hov"
            name="my_box"
            style={{
              width: 'auto',
              bottom: 15,
              right: 15,
            }}
            onClick={!isExporting ? exportFullReportCSV : undefined}
          >
            {isExporting ? (
              <Box
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <CircularProgress
                  size={24}
                  style={{ color: 'var(--text-cta)' }}
                />
                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text style={{ color: 'var(--text-cta)' }}>
                    Exporting: {exportProgress}% complete
                  </Text>
                  <Box
                    style={{
                      width: '100%',
                      height: '4px',
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      borderRadius: '2px',
                      marginTop: '4px',
                    }}
                  >
                    <Box
                      style={{
                        width: `${exportProgress}%`,
                        height: '100%',
                        backgroundColor: 'var(--background-color-2)',
                        borderRadius: '2px',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              'Merge and Export Reports'
            )}
          </Box>
        )}
        <Box className="report-generator-box">
          <Text
            style={{
              fontWeight: 'bold',
              fontSize: '2rem',
              color: 'var(--text-color)',
            }}
          >
            Report Builder
          </Text>
          <Text style={{ fontSize: '1.2rem', color: 'var(--primary-color)' }}>
            Note: Limiting the number of users, KPIs, teams, and timeframes will
            decrease loading time.
          </Text>
          {showReportConfig ? (
            <Box style={{ marginTop: '25px' }}>
              <Box name="options-main-container">
                <Box>
                  <Box
                    name="line-one"
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      alignItems: 'flex-end',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Text
                        style={{
                          fontWeight: 'bold',
                          marginBottom: '2px',
                          color: 'var(--text-color)',
                        }}
                      >
                        Report Type
                      </Text>
                      <StyledSelect
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                      >
                        {reportTypes.map((opt, i) => (
                          <StyledMenuItem value={opt.value} key={i}>
                            {opt.title}
                          </StyledMenuItem>
                        ))}
                      </StyledSelect>
                    </Box>
                    <DateRangePicker
                      fromTime={fromTime}
                      setFromTime={setFromTime}
                      toTime={toTime}
                      setToTime={setToTime}
                      reportType={reportType}
                      rangeCode={rangeCode}
                      setRange={(v) => setRangeCode(v)}
                    />
                  </Box>
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-end',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      name="line-two"
                      style={{
                        marginTop: '1rem',
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                      }}
                    >
                      {reportType !== 'hourly' && (
                        <Box>
                          <Text
                            style={{
                              fontWeight: 'bold',
                              marginBottom: '2px',
                              color: 'var(--text-color)',
                            }}
                          >
                            KPI Selection
                          </Text>
                          <Box
                            className="selection-box hov"
                            onClick={() => setModalType('kpi')}
                          >
                            {Object.keys(checkedKpis).length === 0 ? (
                              <Text
                                style={{ color: 'var(--text-color-light)' }}
                              >
                                {"Click to select KPI's"}
                              </Text>
                            ) : (
                              Object.values(checkedKpis).map((kpi, i) => (
                                <Box
                                  name={kpi}
                                  className="selected-item"
                                  key={i}
                                >
                                  {kpi}
                                </Box>
                              ))
                            )}
                          </Box>
                        </Box>
                      )}
                      {reportType === 'team' && (
                        <Box>
                          <Text
                            style={{
                              fontWeight: 'bold',
                              marginBottom: '2px',
                              color: 'var(--text-color)',
                            }}
                          >
                            Team Selection
                          </Text>
                          <Box
                            className="selection-box hov"
                            onClick={() => setModalType('team')}
                          >
                            {Object.keys(checkedTeams).length === 0 ? (
                              <Text
                                style={{ color: 'var(--text-color-light)' }}
                              >
                                Click to select Teams
                              </Text>
                            ) : (
                              Object.values(checkedTeams).map((team, i) => (
                                <Box
                                  name={team}
                                  className="selected-item"
                                  key={i}
                                >
                                  {team}
                                </Box>
                              ))
                            )}
                          </Box>
                        </Box>
                      )}
                      {(reportType === 'user' || reportType === 'hourly') && (
                        <Box>
                          <Text
                            style={{
                              fontWeight: 'bold',
                              marginBottom: '2px',
                              color: 'var(--text-color)',
                            }}
                          >
                            User Selection
                          </Text>
                          <Box
                            className="selection-box hov"
                            onClick={() => setModalType('user')}
                          >
                            {Object.keys(checkedUsers).length === 0 ? (
                              <Text
                                style={{ color: 'var(--text-color-light)' }}
                              >
                                Click to select Teams
                              </Text>
                            ) : (
                              Object.values(checkedUsers).map((user, i) => (
                                <Box
                                  name={user}
                                  className="selected-item"
                                  key={i}
                                >
                                  {user}
                                </Box>
                              ))
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                    <Box
                      className={`generate-button ${canSubmit ? 'hov' : ''}`}
                      style={{
                        backgroundColor: canSubmit
                          ? 'var(--button-color)'
                          : theme.background3,
                        color: canSubmit ? 'var(--text-cta)' : theme.textLight,
                      }}
                      onClick={() => {
                        if (canSubmit) handleRunReport();
                      }}
                    >
                      {gettingReports ? <CircularProgress /> : 'Generate'}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Text
              style={{ fontWeight: 'bold', color: theme.textLight }}
              onClick={() => setShowReportConfig(true)}
            >
              {reportConfigString()}
            </Text>
          )}
          {myReports && (
            <Box
              className="show-report-config-button hov"
              onClick={() => setShowReportConfig((p) => !p)}
            >
              {showReportConfig ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </Box>
          )}
        </Box>

        {myReports && (
          <ReportsCharts
            reports={myReports}
            filters={{
              reportType,
              checkedTeams,
              checkedUsers,
              checkedKpis,
            }}
          />
        )}
      </View>

      {modalType && (
        <SelectionModal
          config={modalConfiguration[modalType]}
          searchString={searchString}
          setSearchString={setSearchString}
          open={Boolean(modalType)}
          close={() => {
            setModalType(null);
            setSearchString('');
          }}
        />
      )}
    </React.Fragment>
  );
}
